import { useEffect, useRef, useState } from "react";

/*
 * A no with a named condition (`docs/2026-10-03-conditional-no.md`, "The card"): the lead said no
 * unless something holds, so the app wrote no draft and left the question to the rep. The sheet shows
 * the lead's own sentence and asks one thing. Yes redrafts this reply as an answer that says the
 * condition holds; No files it with the other noes, settled in Email Bison as every no is; Not sure
 * writes nothing and keeps the row in Your move.
 */
export function ConditionalNoSheet(props: {
  sentence: string | null;
  busy: boolean;
  onHolds: () => void;
  onNo: () => void;
}) {
  const [left, setLeft] = useState(false);
  // Focus follows the fold, as on the lead-link sheet: the pressed button unmounts, so without this
  // the keyboard lands on the page body. Not sure hands focus to "Answer now", which hands it back to
  // Not sure. Nothing moves on first render, so opening a conversation never pulls focus here.
  const folded = useRef(false);
  const answerNow = useRef<HTMLButtonElement>(null);
  const notSure = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!folded.current) return;
    (left ? answerNow : notSure).current?.focus();
  }, [left]);
  const fold = (next: boolean) => {
    folded.current = true;
    setLeft(next);
  };
  return (
    <article
      className="lcr-sheet lcr-conditional-no"
      aria-labelledby="lcr-conditional-no-heading"
      aria-busy={props.busy}
    >
      <h3 id="lcr-conditional-no-heading">A no, with a condition</h3>
      {props.sentence ? (
        <p className="lcr-conditional-no-quote">
          They wrote: <q>{props.sentence}</q>
        </p>
      ) : null}
      {left ? (
        <p className="lcr-meta-text lcr-conditional-no-left">
          <span id="lcr-conditional-no-left">
            Left for later: it stays in Your move until you answer.
          </span>{" "}
          <button
            ref={answerNow}
            type="button"
            className="lcr-linklike"
            aria-describedby="lcr-conditional-no-left"
            onClick={() => fold(false)}
          >
            Answer now
          </button>
        </p>
      ) : (
        <>
          <fieldset className="lcr-conditional-no-choice">
            <legend className="lcr-conditional-no-question">
              Does that hold for this campaign?
            </legend>
            <div className="lcr-actions lcr-conditional-no-actions">
              <button
                type="button"
                className="lcr-primary"
                disabled={props.busy}
                onClick={props.onHolds}
              >
                Yes: draft a reply on it
              </button>
              <button type="button" disabled={props.busy} onClick={props.onNo}>
                No: it's a no
              </button>
              <button ref={notSure} type="button" disabled={props.busy} onClick={() => fold(true)}>
                Not sure: leave it
              </button>
            </div>
          </fieldset>
          <p className="lcr-meta-text lcr-conditional-no-note">
            Nothing was drafted. Yes drafts a reply that says it holds; nothing is saved for other
            leads.
          </p>
        </>
      )}
    </article>
  );
}

/** Why "Yes" was refused, in words the rep can act on; the route names the code. */
export function conditionHoldsRefusal(code: string | null): string {
  switch (code) {
    case "run_in_flight":
      return "A draft for this reply is already being prepared.";
    case "plan_changed":
    case "current_turn_event_not_reopenable":
      return "The lead wrote again, so this reply can no longer be drafted. Answer the newest message.";
    case "current_turn_sent":
      return "A reply to this message was already sent.";
    case "current_turn_answered":
      return "Someone already answered this message in Bison, so the app will not draft it.";
    case "suppressed":
      return "This lead asked not to be contacted.";
    case "not_conditional_no":
      return "This reply is no longer waiting for your answer.";
    case "condition_holds_unavailable":
      return "Drafting is not running here, so a draft cannot be asked for.";
    default:
      return `The app could not draft this reply (${code ?? "error"}).`;
  }
}
